import { css } from '@emotion/css';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigator } from '../../../routing/navigator';
import { QuestionmarkCircle } from '../../../shared/assets/icons';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../../shared/components/Button';
import { Layout } from '../../../shared/components/layout/Layout';
import { useLocation } from '../../../shared/services/api/location';
import { handlePaymentSubmit } from '../../../shared/services/payment/payment';
import {
  parseAmount,
  hasMaxTwoDecimals,
} from '../../../shared/utils/parseAmount';
import {
  BOTTOM_NAV_SAFE_HEIGHT,
  HEADER_HEIGHT,
} from '../../../shared/styles/css-values';
import {
  doctrineStencilStyle,
  h1Style,
  p2Style,
} from '../../../shared/styles/text';
import { CurrencyAmountSelection } from '../../payment-page/CurrencyAmountSelection';
import { CacheKeys } from '../../../shared/services/api/cache-configuration';
import { http } from '../../../shared/services/http';
import { showPrivateToast } from '../../../shared/utils/show-private-toast';
import { useCurrentUser } from '../../../shared/services/api/user';
import { useActiveAuthorization } from '../../../shared/services/api/payment-authorization';
import { SavedCardSelector } from '../../../shared/components/SavedCardSelector';
import { Loader } from '../../../shared/components/Loader';
import { Colors } from '../../../shared/styles/colors';
import { Radius } from '../../../shared/styles/tokens';

export const WalletAddFundsToLocationPage: React.FC = () => {
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const { locationId } = useParams<{ locationId: string }>();
  const [amount, setAmount] = useState<string>('0');
  // Accepts dot/comma decimals and strips junk ("10 Eur"); null if not a number.
  const parsedAmount = parseAmount(amount);
  const isAmountValid =
    parsedAmount !== null &&
    parsedAmount > 0 &&
    hasMaxTwoDecimals(parsedAmount);
  const [showMoreInfoRefund, setShowMoreInfoRefund] = useState<boolean>(false);
  const [isR1Invoice, setIsR1Invoice] = useState(false);

  const [proceedToPayWaiting, setProceedToPayWaiting] =
    useState<boolean>(false);
  // Saved cards: null means "a new card", which can then be kept for next time.
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [saveCard, setSaveCard] = useState<boolean>(true);
  const {
    data: user,
    isLoading: userIsLoading,
    isError: userIsError,
  } = useCurrentUser();

  if (!locationId) throw new Error();

  const {
    data: location,
    isLoading,
    isError,
  } = useLocation({ id: locationId });

  // This location may also offer paying by card reservation instead of a
  // wallet top-up — make that choice visible here.
  const { data: authInfo } = useActiveAuthorization(Number(locationId));

  if (isError || userIsError) {
    console.error({ isError, userIsError });
    return <p className={errorStyle}>Something went wrong</p>;
  }

  if (isLoading || !location || userIsLoading) {
    return <Loader />;
  }

  // No working card gateway (e.g. a test-only webshop on production): the
  // regular flows never link here, but guard against stale/deep links too.
  if (!location.hasCardPaymentEnabled) {
    return (
      <Layout.Content>
        <div className={innerContainerStyle}>
          <div className={infoContainerStyle}>
            <h1>
              Card payments are not available at{' '}
              <span className={doctrineStencilStyle}>{location.name}</span> yet.
            </h1>
            <p>
              To add funds to your account at this location, please contact the
              staff at the location office.
            </p>
          </div>
        </div>
        <Button
          text={'back'}
          size={ButtonSizeEnum.LARGE}
          variant={ButtonVariantEnum.TEXT}
          onClick={navigator.toPrevious}
        />
      </Layout.Content>
    );
  }

  return (
    <Layout.Content>
      <div className={innerContainerStyle}>
        <div className={infoContainerStyle}>
          <h1>
            Add funds to your{' '}
            <span className={doctrineStencilStyle}>{location.name}</span>{' '}
            account.
          </h1>
          <p>
            Funds you add stay in your wallet and are spent as you use the
            pedestals.
          </p>
        </div>
        {authInfo?.preauthEnabled && !authInfo.authorization && (
          <div className={alternativeOptionStyle}>
            <label>Or pay per use instead</label>
            <p>
              Reserve an amount on your card: nothing is charged up front and
              when your sessions end you pay only what you actually used.
            </p>
            <Button
              size={ButtonSizeEnum.MEDIUM}
              variant={ButtonVariantEnum.OUTLINE}
              text={'reserve funds on card'}
              onClick={() => navigator.toReserveFunds(location.id)}
            />
          </div>
        )}
        <div className={moreInfoStyle}>
          <Button
            startIcon={<QuestionmarkCircle />}
            onClick={() => {
              setShowMoreInfoRefund(!showMoreInfoRefund);
            }}
            text={'More info'}
            size={ButtonSizeEnum.MEDIUM}
            variant={ButtonVariantEnum.TEXT}
          />
          {showMoreInfoRefund && (
            <div>
              <a
                href="/terms-of-use#povrati-sredstava"
                className={policyLinkStyle}
              >
                Payment and refund policy details
              </a>
            </div>
          )}
        </div>
        <CurrencyAmountSelection setAmount={setAmount} />
        <SavedCardSelector
          locationId={location.id}
          selectedCardId={selectedCardId}
          onSelect={setSelectedCardId}
          saveCard={saveCard}
          onSaveCardChange={setSaveCard}
        />
        {user?.r1CompanyAddress &&
        user.r1CompanyName &&
        user.r1VatNumber &&
        user.r1CompanyCountry &&
        user.r1CompanyZipCode &&
        user.r1CompanyCity ? (
          <div className={r1InvoiceDivStyle}>
            <label className={r1InvoiceCheckboxRowStyle}>
              <input
                className={checkboxStyle}
                type="checkbox"
                checked={isR1Invoice}
                onChange={() => setIsR1Invoice(!isR1Invoice)}
              />
              <span>I need company invoice.</span>
            </label>
            {isR1Invoice && (
              <dl className={r1InvoiceDataStyle}>
                <dt>Name</dt>
                <dd>{user?.r1CompanyName}</dd>
                <dt>VAT number</dt>
                <dd>{user?.r1VatNumber}</dd>
                <dt>Address</dt>
                <dd>{user?.r1CompanyAddress}</dd>
                <dt>City</dt>
                <dd>{user?.r1CompanyCity}</dd>
                <dt>Zip code</dt>
                <dd>{user?.r1CompanyZipCode}</dd>
                <dt>Country</dt>
                <dd>{user?.r1CompanyCountry}</dd>
              </dl>
            )}
          </div>
        ) : (
          <div className={r1InvoiceHintStyle}>
            If you need company invoice, please fill the required data in your
            profile.
          </div>
        )}
      </div>
      <div className={buttonActionContainerStyle}>
        <Button
          size={ButtonSizeEnum.LARGE}
          variant={ButtonVariantEnum.TEXT}
          text={'back'}
          onClick={navigator.toPrevious}
        />
        <Button
          size={ButtonSizeEnum.LARGE}
          variant={ButtonVariantEnum.FILLED}
          text={'proceed to pay'}
          disabled={!isAmountValid || proceedToPayWaiting}
          loading={proceedToPayWaiting}
          onClick={async () => {
            if (proceedToPayWaiting) {
              return;
            }
            setProceedToPayWaiting(true);
            try {
              if (user) {
                const account = user.accounts?.find(
                  (a) =>
                    a.location.id === location.id ||
                    a.location.subLocations?.some(
                      (sl) => sl.id === location.id,
                    ),
                );
                if (!account) {
                  await http.createAccount(location.id);
                  await queryClient.refetchQueries({
                    queryKey: [CacheKeys.ME],
                  });
                }
              }
              await handlePaymentSubmit(
                amount,
                location.id,
                isR1Invoice,
                undefined,
                { saveCard, savedCardId: selectedCardId },
              );
            } catch (error) {
              console.error(error);
              const serverMessage = (error as any)?.response?.data?.message;
              showPrivateToast(
                typeof serverMessage === 'string' && serverMessage
                  ? serverMessage
                  : `Payment can't be started right now. Try again later.`,
              );
            } finally {
              setProceedToPayWaiting(false);
            }
          }}
        />
      </div>
    </Layout.Content>
  );
};

const innerContainerStyle = css`
  display: flex;
  flex-flow: column;
  align-items: start;
  gap: 1em;
  margin-top: calc(6.375rem - ${HEADER_HEIGHT});
  min-height: calc(
    100vh - 6.375rem - ${HEADER_HEIGHT} - ${BOTTOM_NAV_SAFE_HEIGHT}
  );
  padding-bottom: 1rem;
`;

const errorStyle = css`
  padding: 2rem 1.5rem;
  color: ${Colors.TEXT_SECONDARY};
`;

const infoContainerStyle = css`
  h1 {
    ${h1Style}
    overflow-wrap: anywhere;
  }
  p {
    ${p2Style}
    color: ${Colors.TEXT_SECONDARY};
    margin-top: 1rem;
  }
`;

const buttonActionContainerStyle = css`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  width: calc(100% + 3rem);
  justify-content: space-between;
  gap: 1rem;
  margin: 0 -1.5rem -2rem;
  padding: 0.75rem 1.5rem calc(0.75rem + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.94);
  -webkit-backdrop-filter: saturate(160%) blur(14px);
  backdrop-filter: saturate(160%) blur(14px);
  border-top: 1px solid ${Colors.BORDER};
`;

const moreInfoStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const policyLinkStyle = css`
  ${p2Style}
  color: ${Colors.CAPRI_BLUE};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.2em;
`;

const r1InvoiceDivStyle = css`
  ${p2Style}
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.md};
`;

const r1InvoiceHintStyle = css`
  ${p2Style}
  color: ${Colors.TEXT_SECONDARY};
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`;

const alternativeOptionStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem;
  background: ${Colors.SURFACE};
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.lg};
  label {
    ${p2Style}
    font-weight: bold;
  }
  p {
    ${p2Style}
    color: ${Colors.TEXT_SECONDARY};
    margin-bottom: 0.25rem;
  }
`;

const r1InvoiceCheckboxRowStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
`;

const r1InvoiceDataStyle = css`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.375rem 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${Colors.BORDER};

  dt {
    color: ${Colors.TEXT_SECONDARY};
  }
  dd {
    overflow-wrap: anywhere;
  }
`;

const checkboxStyle = css`
  width: 20px;
  height: 20px;
  margin: 0;
  flex: 0 0 auto;
  cursor: pointer;
  accent-color: ${Colors.CAPRI_BLUE};
`;
